/**
 * Registre de présentation des spécialités — le miroir front du registre backend.
 *
 * Le backend (`mage_x_api/constants/specialities.py`) est la source de vérité de ce qui EXISTE :
 * il expose `supported_specialites` sur `/configuration`, `/user/early-user-specialites` et
 * `/user/request-access/options`, et chaque entrée porte déjà `id`, `label`, `short_label`,
 * `emoji`, `description` et `features`.
 * Une spécialité ajoutée côté backend s'affiche donc partout sans qu'aucun fichier front ne
 * change : c'est le comportement par défaut, et c'est le but.
 *
 * Ce module n'existe que pour deux raisons :
 *
 *  1. **La traduction.** Les libellés venus de l'API sont du français figé, invisible pour Lingui.
 *     `SPECIALITY_TRANSLATIONS` porte la version traduisible des spécialités connues, de sorte
 *     que `en` et `de` aient de vraies chaînes plutôt que du français. Une spécialité absente de
 *     cette table s'affiche avec le texte de l'API — dégradé, jamais cassé.
 *  2. **Le repli.** Certains écrans (le badge de l'en-tête) affichent une spécialité sans avoir
 *     chargé la liste de l'API. Ils tombent alors sur la table locale, puis sur l'identifiant.
 *
 * Avant, trois composants portaient chacun leur propre `if (id === "rachis")` : en ajouter une
 * quatrième demandait de retrouver trois fichiers qui ne se connaissent pas. Il n'y a plus qu'ici.
 */
import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";

/** Descripteur de spécialité tel que l'API le renvoie. */
export interface SpecialityDescriptor {
  id: string;
  label: string;
  short_label?: string;
  emoji?: string;
  description?: string;
  features?: string[];
}

/** Présentation résolue, prête à afficher. */
export interface SpecialityUi {
  id: string;
  /** Nom complet — menus, cartes d'onboarding. */
  label: string;
  /** Nom court — pastille de l'en-tête, où le libellé complet ne tient pas. */
  shortLabel: string;
  emoji: string;
  description: string;
  features: string[];
}

type Translation = {
  label: MessageDescriptor;
  shortLabel: MessageDescriptor;
  description: MessageDescriptor;
  features: MessageDescriptor[];
};

/**
 * Traductions des spécialités connues. Facultatif : une spécialité absente d'ici reste affichée
 * avec les libellés français de l'API. Les clés doivent rester alignées sur les `id` du registre
 * backend.
 */
const SPECIALITY_TRANSLATIONS: Record<string, Translation> = {
  rachis: {
    label: msg`Chirurgie du rachis`,
    shortLabel: msg`Rachis`,
    description: msg`Rachis cervical et lombaire — scores ODI, EVA, NPRS, Eurospine.`,
    features: [
      msg`Pipeline rachis`,
      msg`Ontologie ODI / EVA / NPRS`,
      msg`Export EuroSpine (Spine Tango)`,
    ],
  },
  "orthopedie-membre-inferieur": {
    label: msg`Orthopédie du membre inférieur`,
    shortLabel: msg`Membre inférieur`,
    description: msg`Hanche et genou — arthroplastie, reprise, suivi per et post-opératoire.`,
    features: [
      msg`Lecture des comptes-rendus opératoires`,
      msg`Consultation pré-anesthésique (ASA, comorbidités)`,
      msg`Implants, voie d'abord et latéralité`,
    ],
  },
  "pre-natal": {
    label: msg`Médecine fœto-maternelle`,
    shortLabel: msg`Pré-natal`,
    description: msg`Suivi anténatal — dépistage, échographies, pathologies fœtales.`,
    features: [msg`Pipeline d'extraction adapté à votre spécialité`],
  },
  "gynecologie-obstetrique": {
    label: msg`Gynécologie-obstétrique`,
    shortLabel: msg`Gynéco-obstétrique`,
    description: msg`Gynécologie de ville et suivi de grossesse — contraception, échographie, dépistage, ménopause.`,
    features: [
      msg`Import des comptes rendus HL7`,
      msg`Échographie pelvienne et obstétricale`,
      msg`Contraception, ménopause et dépistage du col`,
    ],
  },
};

/** Emoji affiché quand ni l'API ni la table locale n'en proposent un. */
const DEFAULT_EMOJI = "🩺";

type Translator = (descriptor: MessageDescriptor) => string;

/**
 * Résout la présentation d'une spécialité.
 *
 * @param id           identifiant de la spécialité (`User.specialite`).
 * @param t            fonction de traduction Lingui (`useLingui().t`).
 * @param descriptor   entrée renvoyée par l'API, quand l'écran l'a chargée.
 *
 * L'ordre de préférence est : traduction locale → descripteur de l'API → identifiant brut.
 * Aucune étape n'est obligatoire, donc une spécialité inconnue des deux côtés s'affiche encore
 * sous son identifiant plutôt que sous une case vide.
 */
export function getSpecialityUi(
  id: string,
  t: Translator,
  descriptor?: SpecialityDescriptor | null,
): SpecialityUi {
  const translation = SPECIALITY_TRANSLATIONS[id];
  const translatedFeatures = translation?.features.map((f) => t(f));

  return {
    id,
    label: translation ? t(translation.label) : (descriptor?.label ?? id),
    shortLabel: translation
      ? t(translation.shortLabel)
      : (descriptor?.short_label ?? descriptor?.label ?? id),
    emoji: descriptor?.emoji ?? DEFAULT_EMOJI,
    description: translation
      ? t(translation.description)
      : (descriptor?.description ?? ""),
    features: translatedFeatures ?? descriptor?.features ?? [],
  };
}

/** Libellé complet seul — le besoin le plus fréquent. */
export function specialityLabel(
  id: string | null | undefined,
  t: Translator,
  descriptor?: SpecialityDescriptor | null,
): string {
  if (!id) return "";
  return getSpecialityUi(id, t, descriptor).label;
}

/** Libellé court seul — pastille de l'en-tête. */
export function specialityShortLabel(
  id: string | null | undefined,
  t: Translator,
  descriptor?: SpecialityDescriptor | null,
): string {
  if (!id) return "";
  return getSpecialityUi(id, t, descriptor).shortLabel;
}
